import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Address, Hex } from "viem";
import type { PrivateKeyAccount } from "viem/accounts";
import { MAX_UINT256, type ChainWriter, type ExchangedEvent } from "@woodpecker/chain";
import {
  defaultSettings,
  headMemoryOf,
  isSettingsPointer,
  settingsWrites,
  type ConversationSummary,
  type SettingsRecord,
  type InstallRecord,
} from "@woodpecker/protocol";
import type { ConsoleConfig } from "../lib/config";
import { round2, wpOf, wpPerToken } from "../lib/format";
import { chainOf, loadDirectory, tokenMetaOf, type ServiceEntry, type TokenMeta } from "../adapters/chain";
import { HttpError, MemoryClient, RunnerClient, type ConversationView } from "../adapters/clients";
import { consoleIdentityOf, extensionIdentityOf, forgetRoots, rootOf } from "../adapters/identity";
import { paidSettingsWrite } from "../adapters/settings-write";
import {
  clearSessions,
  createWallet,
  forgetWallet,
  loadWallet,
  saveWallet,
  sessionOf,
  type BrowserWallet,
} from "../adapters/wallet";

/** One row of a conversation's authorizations: cap and used in whole tokens (wpCredit in wp). */
export interface AuthRow {
  token: string;
  tokenAddress: Address;
  cap: number;
  used: number;
  remaining: number;
  capRaw: bigint;
  usedRaw: bigint;
}

export type Theme = "light" | "dark";
const THEME_KEY = "woodpecker-theme";
/** The order of the extensions' menu items, this browser's own (D69, D50a). */
const MENU_KEY = "woodpecker-menu-order";

function readMenuOrder(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(MENU_KEY) ?? "[]") as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function readTheme(): Theme {
  try {
    return localStorage.getItem(THEME_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

export interface Store {
  config: ConsoleConfig;
  chain: ChainWriter;
  wallet: BrowserWallet | null;
  setWalletKey: (key: `0x${string}`) => void;
  newWallet: () => BrowserWallet;
  forget: () => void;
  theme: Theme;
  setTheme: (t: Theme) => void;
  // the directory and the tokens
  directory: ServiceEntry[];
  directoryLoading: boolean;
  directoryError: string | null;
  refreshDirectory: () => Promise<ServiceEntry[]>;
  tokens: Map<string, TokenMeta>;
  entry: (name: string) => ServiceEntry | undefined;
  tokenOf: (symbol: string) => TokenMeta | undefined;
  wpPerToken: (symbol: string) => number; // wp per 1 token at today's rate
  // the wallet's chain state
  balances: Record<string, number>; // symbol → whole tokens (wpCredit in wp)
  balancesRaw: Record<string, bigint>;
  ethBalance: bigint;
  refreshBalances: () => Promise<void>;
  exchanges: ExchangedEvent[];
  // sessions
  signedIn: boolean;
  setSignedIn: (v: boolean) => void;
  signOut: () => void;
  runnerOrigin: string | null;
  memoryOrigin: string | null;
  runner: RunnerClient | null;
  memory: MemoryClient | null;
  // the settings record (D50) and My services
  settings: SettingsRecord;
  settingsLoaded: boolean;
  saveSettings: (patch: Partial<SettingsRecord>) => Promise<void>;
  /** The provider's memory: it keeps the pointer to the memory that holds the record (issue #2). */
  headMemory: string;
  /** Whether the browser holds a session with a memory service, by name. */
  sessionWith: (name: string) => boolean;
  /** A memory service's owner client by name (null when the directory does not list it); its session is the browser's. */
  clientFor: (name: string) => MemoryClient | null;
  /** Switches the default memory: the full record to the new memory, the pointer to the head; needs sessions with both. */
  switchMemory: (next: string, patch?: Partial<SettingsRecord>) => Promise<void>;
  /** The head points at a memory the directory does not list any more: the record is out of reach until a switch. */
  settingsProblem: { pointer: string } | null;
  /** The identity root of this browser for the wallet (D73): null until the wallet signed the root message. */
  identityRoot: Hex | null;
  setIdentityRoot: (root: Hex) => void;
  /** The install records of the settings record (D72, D74). */
  installed: InstallRecord[];
  /** The ids of the extensions' menu items in this browser's order; the rest follow in install order (D69). */
  menuOrder: string[];
  setMenuOrder: (ids: string[]) => void;
  installExtension: (record: InstallRecord) => Promise<void>;
  uninstallExtension: (id: string) => Promise<void>;
  setExtensionEnabled: (id: string, enabled: boolean) => Promise<void>;
  /** An installed extension's identity, derived from the root this browser holds (D73); null without a root. */
  extensionIdentityFor: (id: string) => PrivateKeyAccount | null;
  /** The wallet's handle in the ServiceDirectory (owner@addr4), or null. */
  walletHandle: string | null;
  /** The Console's own identity, derived from the root: it pays the writes of the settings record (D75). */
  consoleIdentity: PrivateKeyAccount | null;
  /** The last confirmed write of the settings record in this session: the memory and the settle's tx. */
  lastSettingsWrite: { memory: string; tx: Hex; at: string } | null;
  /** The cap and the use of the Console's identity in a memory's token (D75), from contract A; null without an identity. */
  payerAuthorization: (
    memoryName: string,
  ) => Promise<{ token: string; cap: number; used: number; price: number } | null>;
  myServices: ServiceEntry[];
  // conversations
  conversations: ConversationView[];
  memoryList: ConversationSummary[];
  refreshConversations: () => Promise<void>;
  setConversation: (v: ConversationView) => void;
  auths: Record<string, AuthRow[]>;
  refreshAuth: (conversationId: string, agent: Address) => Promise<AuthRow[]>;
  refreshAll: () => Promise<void>;
  approvedTokens: Set<string>; // symbols with an allowance to contract A
}

const StoreContext = createContext<Store | null>(null);

export function StoreProvider({ config, children }: { config: ConsoleConfig; children: ReactNode }) {
  const [wallet, setWallet] = useState<BrowserWallet | null>(loadWallet);
  const [theme, setThemeState] = useState<Theme>(readTheme);
  const chain = useMemo(() => chainOf(config, wallet?.account), [config, wallet]);
  const [directory, setDirectory] = useState<ServiceEntry[]>([]);
  const [directoryLoading, setDirectoryLoading] = useState(true);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [balancesRaw, setBalancesRaw] = useState<Record<string, bigint>>({});
  const [ethBalance, setEthBalance] = useState(0n);
  const [exchanges, setExchanges] = useState<ExchangedEvent[]>([]);
  const [signedIn, setSignedIn] = useState(false);
  const [settings, setSettings] = useState<SettingsRecord>(() => defaultSettings(config.provider));
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [settingsProblem, setSettingsProblem] = useState<{ pointer: string } | null>(null);
  const [identityRoot, setIdentityRootState] = useState<Hex | null>(() =>
    wallet ? rootOf(config.chainId, wallet.address) : null,
  );
  const [lastSettingsWrite, setLastSettingsWrite] = useState<{ memory: string; tx: Hex; at: string } | null>(null);
  const [menuOrder, setMenuOrderState] = useState<string[]>(readMenuOrder);
  const [walletHandle, setWalletHandle] = useState<string | null>(null);
  const [conversations, setConversations] = useState<ConversationView[]>([]);
  const [memoryList, setMemoryList] = useState<ConversationSummary[]>([]);
  const [auths, setAuths] = useState<Record<string, AuthRow[]>>({});
  const [approvedTokens, setApproved] = useState<Set<string>>(new Set());
  const directoryRef = useRef<ServiceEntry[]>([]);
  // the memories read while following the settings pointer since the last record (a reader never spins)
  const hops = useRef<string[]>([]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* no storage */
    }
  }, [theme]);

  // the identity root follows the wallet: the one this browser keeps for it, or none until the sign-in signs it
  useEffect(() => {
    setIdentityRootState(wallet ? rootOf(config.chainId, wallet.address) : null);
  }, [wallet, config.chainId]);
  const consoleIdentity = useMemo(() => (identityRoot ? consoleIdentityOf(identityRoot) : null), [identityRoot]);
  const extensionIdentityFor = useCallback(
    (id: string): PrivateKeyAccount | null => {
      const root = identityRoot ?? (wallet ? rootOf(config.chainId, wallet.address) : null);
      return root ? extensionIdentityOf(root, id) : null;
    },
    [identityRoot, wallet, config.chainId],
  );
  const setMenuOrder = useCallback((ids: string[]) => {
    setMenuOrderState(ids);
    try {
      localStorage.setItem(MENU_KEY, JSON.stringify(ids));
    } catch {
      /* no storage */
    }
  }, []);
  // the wallet's handle, read from the directory (D42)
  useEffect(() => {
    if (!wallet) {
      setWalletHandle(null);
      return;
    }
    let on = true;
    chain
      .dirHandleOf(wallet.address)
      .then((h) => {
        if (on) setWalletHandle(h.exists ? `${h.name}@${h.addr4}` : null);
      })
      .catch(() => {
        if (on) setWalletHandle(null);
      });
    return () => {
      on = false;
    };
  }, [chain, wallet]);

  const tokens = useMemo(() => tokenMetaOf(directory, config.wpCredit), [directory, config.wpCredit]);
  const tokenOf = useCallback((symbol: string) => tokens.get(symbol), [tokens]);
  const entry = useCallback((name: string) => directory.find((e) => e.name === name), [directory]);
  const wpPer = useCallback(
    (symbol: string) => {
      if (symbol === "wpCredit") return 1;
      const t = tokens.get(symbol);
      return t ? wpPerToken(t.rate) : 1;
    },
    [tokens],
  );

  const refreshDirectory = useCallback(async () => {
    setDirectoryLoading(true);
    setDirectoryError(null);
    try {
      const entries = await loadDirectory(chain, wallet?.address ?? null);
      directoryRef.current = entries;
      setDirectory(entries);
      return entries;
    } catch (e) {
      setDirectoryError(e instanceof Error ? e.message : String(e));
      return directoryRef.current;
    } finally {
      setDirectoryLoading(false);
    }
  }, [chain, wallet]);

  const refreshBalances = useCallback(async () => {
    if (!wallet) return;
    const metas = tokenMetaOf(directoryRef.current, config.wpCredit);
    const raw: Record<string, bigint> = {};
    const approved = new Set<string>();
    await Promise.all(
      [...metas.values()].map(async (t) => {
        raw[t.symbol] = await chain.balanceOf(t.address, wallet.address);
        const allowance = await chain.allowance(t.address, wallet.address, config.contractA);
        if (allowance >= MAX_UINT256 / 2n) approved.add(t.symbol);
      }),
    );
    setBalancesRaw(raw);
    setApproved(approved);
    setEthBalance(await chain.ethBalance(wallet.address));
    const ex: ExchangedEvent[] = [];
    for (const t of metas.values()) {
      if (t.symbol === "wpCredit") continue;
      try {
        for (const e of await chain.exchangedEvents(t.address))
          if (e.from.toLowerCase() === wallet.address.toLowerCase()) ex.push(e);
      } catch {
        /* a token without events */
      }
    }
    setExchanges(ex);
  }, [chain, wallet, config]);

  const balances = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [sym, v] of Object.entries(balancesRaw)) out[sym] = sym === "wpCredit" ? wpOf(v) : Number(v);
    return out;
  }, [balancesRaw]);

  // the provider's defaults (D43): the provider named in config.json, the protocol constant as the fallback
  const providerDefaults = useMemo(() => defaultSettings(config.provider), [config.provider]);
  const headMemory = useMemo(() => headMemoryOf(config.provider), [config.provider]);
  const runnerName = settings.defaultRunner || providerDefaults.defaultRunner;
  const memoryName = settings.defaultMemory || providerDefaults.defaultMemory;
  const runnerOrigin = entry(runnerName)?.origin ?? null;
  const memoryOrigin = entry(memoryName)?.origin ?? null;
  const runner = useMemo(
    () => (wallet && runnerOrigin ? new RunnerClient(runnerOrigin, wallet.address, config.chainId) : null),
    [wallet, runnerOrigin, config.chainId],
  );
  const memory = useMemo(
    () => (wallet && memoryOrigin ? new MemoryClient(memoryOrigin, wallet.address, config.chainId) : null),
    [wallet, memoryOrigin, config.chainId],
  );

  // signed in = both sessions are valid
  useEffect(() => {
    if (!wallet || !runnerOrigin || !memoryOrigin) {
      setSignedIn(false);
      return;
    }
    setSignedIn(!!sessionOf(runnerOrigin, wallet.address) && !!sessionOf(memoryOrigin, wallet.address));
  }, [wallet, runnerOrigin, memoryOrigin]);

  const rowsOf = useCallback(
    async (agent: Address): Promise<AuthRow[]> => {
      if (!wallet) return [];
      const list = await chain.tokensOf(wallet.address, agent);
      const metas = tokenMetaOf(directoryRef.current, config.wpCredit);
      const byAddress = new Map([...metas.values()].map((t) => [t.address.toLowerCase(), t]));
      const rows: AuthRow[] = [];
      for (const addr of list) {
        const a = await chain.authorizationOf(wallet.address, agent, addr);
        let meta = byAddress.get(addr.toLowerCase());
        if (!meta) {
          const info = await chain.tokenInfo(addr);
          meta = {
            symbol: info.symbol,
            address: addr,
            rate: info.rate,
            decimals: info.decimals,
            owner: info.owner,
            service: info.service,
          };
        }
        const wp = meta.symbol === "wpCredit";
        const cap = wp ? wpOf(a.cap) : Number(a.cap);
        const used = wp ? wpOf(a.used) : Number(a.used);
        rows.push({
          token: meta.symbol,
          tokenAddress: addr,
          cap,
          used,
          remaining: Math.max(0, round2(cap - used)),
          capRaw: a.cap,
          usedRaw: a.used,
        });
      }
      return rows;
    },
    [chain, wallet, config.wpCredit],
  );

  const refreshAuth = useCallback(
    async (conversationId: string, agent: Address) => {
      const rows = await rowsOf(agent);
      setAuths((prev) => ({ ...prev, [conversationId]: rows }));
      return rows;
    },
    [rowsOf],
  );

  const refreshConversations = useCallback(async () => {
    if (!runner || !memory || !signedIn) return;
    const [views, list] = await Promise.all([
      runner.list().catch(() => [] as ConversationView[]),
      memory.list().catch(() => [] as ConversationSummary[]),
    ]);
    views.sort((a, b) => (a.lastActivity < b.lastActivity ? 1 : -1));
    setConversations(views);
    setMemoryList(list);
    const rows: Record<string, AuthRow[]> = {};
    await Promise.all(
      views.map(async (v) => {
        rows[v.id] = await rowsOf(v.agent).catch(() => []);
      }),
    );
    setAuths((prev) => ({ ...prev, ...rows }));
  }, [runner, memory, signedIn, rowsOf]);

  /**
   * The settings record (D50, issue #2): the head memory keeps only the pointer `defaultMemory`; the memory it names
   * keeps the record. A record that names another memory is a pointer: the Console moves there (the memory client
   * changes) and reads again; a record that names the memory it came from is the record. The sign-in flow follows
   * the same pointer and signs in to the memory it names in the same request (issue #7 item 2), so the move finds
   * a session; without one, the wallet block asks for that one signature.
   */
  const loadSettings = useCallback(async () => {
    if (!memory || !signedIn) return;
    try {
      const s = await memory.settings();
      const next = s.defaultMemory;
      if (isSettingsPointer(s, memoryName) && !hops.current.includes(next) && hops.current.length < 4) {
        if (!entry(next)) {
          // the memory the pointer names is not in the directory: not loaded yet while the directory loads; gone
          // when it has loaded. Either way the pointer-only record is never taken as the settings, so a save cannot
          // overwrite the pointer; a switch on the Settings page moves the record elsewhere.
          setSettingsLoaded(false);
          setSettingsProblem(directoryLoading ? null : { pointer: next });
          return;
        }
        hops.current = [...hops.current, memoryName];
        setSettingsProblem(null);
        setSettings((prev) => ({ ...prev, defaultMemory: next }));
        setSettingsLoaded(false);
        return;
      }
      hops.current = [];
      setSettingsProblem(null);
      setSettings({ ...providerDefaults, ...s, defaultMemory: memoryName });
      setSettingsLoaded(true);
    } catch {
      hops.current = [];
      setSettingsLoaded(false);
    }
  }, [memory, signedIn, providerDefaults, memoryName, entry, directoryLoading]);

  const clientFor = useCallback(
    (name: string): MemoryClient | null => {
      const e = entry(name);
      return wallet && e ? new MemoryClient(e.origin, wallet.address, config.chainId) : null;
    },
    [entry, wallet, config.chainId],
  );

  const sessionWith = useCallback(
    (name: string): boolean => {
      const e = entry(name);
      return !!wallet && !!e && !!sessionOf(e.origin, wallet.address);
    },
    [entry, wallet],
  );

  /**
   * The switch: the full record goes to the new memory with `defaultMemory` = itself, then the pointer to the head
   * (one write when the new memory is the head). Both need a session (the pages sign in through the panel first).
   * Conversation records stay in the memory that stored them; the runner's list names each conversation's memory.
   */
  /**
   * One write of the settings record (D75): a commit paid by the Console's identity, held by the memory until its
   * settle; the confirmed record comes back. The identity's cap in the memory's token is the pages' business
   * (useEnsurePayerCap) before the write.
   */
  const writeSettings = useCallback(
    async (name: string, client: MemoryClient, body: Partial<SettingsRecord>, replace: boolean) => {
      if (!wallet) throw new Error("no wallet");
      // the identity from the state, or from the root the panel just stored (a state update may still be pending)
      const root = identityRoot ?? rootOf(config.chainId, wallet.address);
      const payer = root ? consoleIdentityOf(root) : null;
      if (!payer) throw new Error("this browser holds no identity root yet: sign in first");
      const e = entry(name);
      if (!e) throw new Error(`${name} is not in the directory`);
      const r = await paidSettingsWrite({
        memory: client,
        entry: e,
        wallet: wallet.address,
        payer,
        chainId: config.chainId,
        contractA: config.contractA,
        body,
        replace,
      });
      setLastSettingsWrite({ memory: name, tx: r.tx, at: new Date().toISOString() });
      return r.settings;
    },
    [wallet, identityRoot, entry, config.chainId, config.contractA],
  );

  const payerAuthorization = useCallback(
    async (memoryName: string) => {
      const e = entry(memoryName);
      const root = identityRoot ?? (wallet ? rootOf(config.chainId, wallet.address) : null);
      const payer = root ? consoleIdentityOf(root) : null;
      if (!wallet || !payer || !e) return null;
      const a = await chain.authorizationOf(wallet.address, payer.address, e.tokenAddress);
      return { token: e.token, cap: Number(a.cap), used: Number(a.used), price: e.price.max };
    },
    [wallet, identityRoot, entry, chain, config.chainId],
  );

  const switchMemory = useCallback(
    async (next: string, patch: Partial<SettingsRecord> = {}) => {
      if (!wallet) throw new Error("no wallet");
      const record = { ...settings, ...patch };
      const writes = settingsWrites(record, headMemory, next);
      const clients = writes.map((w) => {
        const c = clientFor(w.memory);
        if (!c) throw new Error(`${w.memory} is not in the directory`);
        if (!c.session) throw new HttpError(401, "no_session", { memory: w.memory });
        return c;
      });
      for (let i = 0; i < writes.length; i++)
        await writeSettings(writes[i]!.memory, clients[i]!, writes[i]!.body, writes[i]!.replace);
      hops.current = [];
      setSettingsProblem(null);
      setSettings({ ...providerDefaults, ...record, defaultMemory: next });
      setSettingsLoaded(true);
    },
    [wallet, settings, headMemory, clientFor, providerDefaults, writeSettings],
  );

  const saveSettings = useCallback(
    async (patch: Partial<SettingsRecord>) => {
      if (patch.defaultMemory && patch.defaultMemory !== memoryName) return switchMemory(patch.defaultMemory, patch);
      // the record is in a memory the directory does not hold (a pointer gone stale, issue #11 B): the save moves the
      // record to the head with the patch applied, instead of refusing until the pointer is replaced
      if (settingsProblem) return switchMemory(headMemory, patch);
      const next = { ...settings, ...patch, defaultMemory: memoryName };
      setSettings(next);
      if (memory && signedIn) {
        const saved = await writeSettings(memoryName, memory, next, false);
        setSettings({ ...providerDefaults, ...saved, defaultMemory: memoryName });
      }
    },
    [
      settings,
      memory,
      signedIn,
      providerDefaults,
      memoryName,
      headMemory,
      switchMemory,
      settingsProblem,
      writeSettings,
    ],
  );

  // the installed extensions live in the settings record (D74): each change is one paid commit (D75)
  const installed = useMemo(() => settings.extensions ?? [], [settings.extensions]);
  const installExtension = useCallback(
    (record: InstallRecord) =>
      saveSettings({ extensions: [...(settings.extensions ?? []).filter((x) => x.id !== record.id), record] }),
    [saveSettings, settings.extensions],
  );
  const uninstallExtension = useCallback(
    (id: string) => saveSettings({ extensions: (settings.extensions ?? []).filter((x) => x.id !== id) }),
    [saveSettings, settings.extensions],
  );
  const setExtensionEnabled = useCallback(
    (id: string, enabled: boolean) =>
      saveSettings({ extensions: (settings.extensions ?? []).map((x) => (x.id === id ? { ...x, enabled } : x)) }),
    [saveSettings, settings.extensions],
  );

  const refreshAll = useCallback(async () => {
    await refreshDirectory();
    await refreshBalances();
    await loadSettings();
    await refreshConversations();
  }, [refreshDirectory, refreshBalances, loadSettings, refreshConversations]);

  // first load: the directory, then the wallet's balances
  useEffect(() => {
    void (async () => {
      await refreshDirectory();
      await refreshBalances();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chain]);

  // after sign-in: the settings record and the conversations; again once the directory has loaded, because a pointer
  // may name a memory the directory did not list yet
  useEffect(() => {
    if (!signedIn) return;
    void (async () => {
      await loadSettings();
      await refreshConversations();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, runner, memory, directoryLoading]);

  // the services this wallet chose, then its own (the directory's entries owned by the wallet: tokensOf(owner), deploy.html step 11)
  const myServices = useMemo(() => {
    const chosen = settings.myServices.flatMap((n) => directory.find((e) => e.name === n) ?? []);
    const own = directory.filter((e) => e.mine && !chosen.some((c) => c.name === e.name));
    return [...chosen, ...own];
  }, [settings.myServices, directory]);

  const setConversation = useCallback((v: ConversationView) => {
    setConversations((prev) => {
      const i = prev.findIndex((c) => c.id === v.id);
      const next = i < 0 ? [v, ...prev] : prev.map((c) => (c.id === v.id ? v : c));
      next.sort((a, b) => (a.lastActivity < b.lastActivity ? 1 : -1));
      return next;
    });
  }, []);

  const value = useMemo<Store>(
    () => ({
      config,
      chain,
      wallet,
      setWalletKey: (key) => setWallet(saveWallet(key)),
      newWallet: () => {
        const w = createWallet();
        setWallet(w);
        return w;
      },
      forget: () => {
        forgetWallet();
        forgetRoots();
        setWallet(null);
        setSignedIn(false);
        setConversations([]);
        setAuths({});
        setBalancesRaw({});
      },
      theme,
      setTheme: setThemeState,
      directory,
      directoryLoading,
      directoryError,
      refreshDirectory,
      tokens,
      entry,
      tokenOf,
      wpPerToken: wpPer,
      balances,
      balancesRaw,
      ethBalance,
      refreshBalances,
      exchanges,
      signedIn,
      setSignedIn,
      signOut: () => {
        clearSessions();
        setSignedIn(false);
        setConversations([]);
        setSettingsLoaded(false);
        setSettingsProblem(null);
        hops.current = [];
        setSettings(providerDefaults);
      },
      runnerOrigin,
      memoryOrigin,
      runner,
      memory,
      settings,
      settingsLoaded,
      saveSettings,
      headMemory,
      sessionWith,
      clientFor,
      switchMemory,
      settingsProblem,
      identityRoot,
      setIdentityRoot: setIdentityRootState,
      consoleIdentity,
      lastSettingsWrite,
      payerAuthorization,
      myServices,
      conversations,
      memoryList,
      refreshConversations,
      setConversation,
      auths,
      refreshAuth,
      refreshAll,
      installed,
      menuOrder,
      setMenuOrder,
      installExtension,
      uninstallExtension,
      setExtensionEnabled,
      extensionIdentityFor,
      walletHandle,
      approvedTokens,
    }),
    [
      config,
      chain,
      wallet,
      theme,
      directory,
      directoryLoading,
      directoryError,
      refreshDirectory,
      tokens,
      entry,
      tokenOf,
      wpPer,
      balances,
      balancesRaw,
      ethBalance,
      refreshBalances,
      exchanges,
      signedIn,
      runnerOrigin,
      memoryOrigin,
      runner,
      memory,
      settings,
      settingsLoaded,
      saveSettings,
      headMemory,
      sessionWith,
      clientFor,
      switchMemory,
      settingsProblem,
      identityRoot,
      consoleIdentity,
      lastSettingsWrite,
      payerAuthorization,
      myServices,
      conversations,
      memoryList,
      refreshConversations,
      setConversation,
      auths,
      refreshAuth,
      refreshAll,
      approvedTokens,
      providerDefaults,
      installed,
      menuOrder,
      setMenuOrder,
      installExtension,
      uninstallExtension,
      setExtensionEnabled,
      extensionIdentityFor,
      walletHandle,
    ],
  );
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): Store {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore needs StoreProvider");
  return ctx;
}
